import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemGroup,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonListHeader,
  IonPage,
  IonThumbnail,
  IonToolbar,
} from '@ionic/react'
import {
  cashOutline,
  cashSharp,
  pricetagsOutline,
  pricetagsSharp,
  pulseOutline,
} from 'ionicons/icons'
import {
  GetShoppingCart,
  PostShoppingCartItem,
  PostShopPurchase,
} from 'web-server'
import { assets } from '../assets'
import { ErrorMessage } from '../components/ErrorMessage'
import { wrapToken } from '../components/wrap-token'
import { PageTitle } from '../components/PageTitle'
import { postAPI, toImageUrl } from '../global/api'
import { routes } from '../global/routes'
import { useGet } from '../hooks/use-get'
import { useObject } from '../hooks/use-object'
import styles from './ShoppingCartCheckoutPage.module.scss'
import { config } from '../config'
import ShopQuestionModal2 from './ShopQuestionModal2'
import { Tips } from '../components/Tips'

export const ShoppingCartCheckoutPage = wrapToken(({ token }) => {
  const shoppingCart = useGet<typeof GetShoppingCart>('shopping-cart', {
    token,
  })
  const state = useObject({
    submit_error: '',
    cart_error: '',
    purchase_id: 0,
    is_finish_shop: false,
  })
  const { purchase_id } = state.value
  const setCart = (
    item: typeof GetShoppingCart['output']['item_list'][number],
    delta: number,
  ) => {
    state.set('cart_error', '')
    postAPI<typeof PostShoppingCartItem>('shopping-cart-item', {
      token,
      item_id: item.id,
      delta,
    }).then(output => {
      state.set('cart_error', output.error)
      if (!output.error) {
        shoppingCart.reload()
      }
    })
  }
  const checkout = (cart_id_list: number[]) => {
    postAPI<typeof PostShopPurchase>('shop-purchase', {
      token,
      cart_id_list,
    }).then(output => {
      state.set('submit_error', output.error)
      state.set('purchase_id', output.purchase_id)
      state.set('is_finish_shop', output.is_finish_shop)
    })
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonBackButton defaultHref={routes.shop.home} />
          </IonButtons>
          <PageTitle>Shopping Cart</PageTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Tips text="After checkout, benchmark your purchasing items against the nutrition principles for diabetes." />

        <img
          src={assets.casher}
          alt="cashier illustration"
          className={styles.cashierBanner}
        />
        {shoppingCart.render({
          name: 'Shopping Cart',
          render: data => {
            const item_list = data.item_list.filter(item => item.quantity > 0)
            let total_price = 0
            let total_bonus = 0
            item_list.forEach(item => {
              total_price += item.price * item.quantity
              if (item.is_healthy) {
                total_bonus += (item.price * item.quantity) / 2
              }
            })
            let remind_coins = data.user.coins - total_price + total_bonus
            return (
              <>
                <IonList className="ion-padding-horizontal">
                  <IonListHeader>Items in Shopping Cart</IonListHeader>
                  {item_list.length === 0 ? (
                    <div className="ion-text-center ion-margin">
                      <ErrorMessage error="No Items in Cart" />
                    </div>
                  ) : null}
                  {item_list.map(item => (
                    <IonItemGroup className="ion-margin-bottom" key={item.id}>
                      <IonItemSliding>
                        <IonItemOptions side="end">
                          <IonItemOption
                            color="primary"
                            onClick={() => setCart(item, +1)}
                          >
                            +1
                          </IonItemOption>
                          <IonItemOption
                            color="dark"
                            onClick={() => setCart(item, -1)}
                          >
                            &nbsp;-1&nbsp;
                          </IonItemOption>
                          <IonItemOption
                            color="danger"
                            onClick={() => setCart(item, -item.quantity)}
                          >
                            Remove
                          </IonItemOption>
                        </IonItemOptions>
                        <IonItem>
                          <IonThumbnail slot="start">
                            <img
                              src={toImageUrl(item.image_url)}
                              alt={'illustration of ' + item.name}
                            />
                          </IonThumbnail>
                          <IonLabel className="ion-text-wrap">
                            {item.name}
                          </IonLabel>
                          <IonLabel slot="end">x {item.quantity}</IonLabel>
                        </IonItem>
                      </IonItemSliding>
                      <div className="ion-text-end">
                        <span>
                          <IonIcon color="warning" icon={cashSharp} /> cost{' '}
                          {item.price * item.quantity} coins
                        </span>
                        <span
                          className="ion-padding-start"
                          hidden={
                            !item.is_healthy || config.hideHealthyFoodHint
                          }
                        >
                          <IonIcon
                            color="success"
                            icon={pricetagsSharp}
                          ></IonIcon>{' '}
                          earn {(item.price * item.quantity) / 2} coins
                        </span>
                      </div>
                    </IonItemGroup>
                  ))}
                  <IonListHeader>Purchase Total</IonListHeader>
                  <IonItem>
                    <IonIcon
                      color="warning"
                      icon={cashOutline}
                      slot="start"
                    ></IonIcon>
                    <IonLabel>cost {total_price} coins</IonLabel>
                  </IonItem>
                  <IonItem hidden={config.hideHealthyFoodHint}>
                    <IonIcon
                      color="success"
                      icon={pricetagsOutline}
                      slot="start"
                    ></IonIcon>
                    <IonLabel>earn {total_bonus} coins</IonLabel>
                  </IonItem>
                  <IonListHeader>After Purchase</IonListHeader>
                  <IonItem>
                    <IonIcon
                      color="warning"
                      icon={cashOutline}
                      slot="start"
                    ></IonIcon>
                    <IonLabel>remain {remind_coins} coins</IonLabel>
                  </IonItem>
                  <IonItem hidden>
                    <IonIcon
                      color="success"
                      icon={pulseOutline}
                      slot="start"
                    ></IonIcon>
                    saved {total_bonus} coins
                  </IonItem>
                </IonList>
                <ErrorMessage
                  error={
                    remind_coins < 0
                      ? `Not enough coins, need ${-remind_coins} more coins`
                      : state.value.cart_error
                  }
                />
                <IonButton
                  className="ion-margin"
                  expand="block"
                  onClick={() => checkout(data.cart_id_list)}
                  disabled={item_list.length === 0 || remind_coins < 0}
                >
                  Checkout
                </IonButton>
                <ErrorMessage error={state.value.submit_error} />
              </>
            )
          },
        })}
        {purchase_id ? (
          <ShopQuestionModal2
            key={purchase_id}
            is_finish_shop={state.value.is_finish_shop}
            token={token}
            purchase_id={state.value.purchase_id}
            sport_batch_id={undefined}
          />
        ) : null}
      </IonContent>
    </IonPage>
  )
})
